import { FIGHT } from '../config/fight';
import { FOOTWORK_EVADE, GUARD_BLOCK, MAX_RANK, PERKS, SKILLS, type PerkId, type SkillId, type SkillRanks } from '../config/skills';
import type { FighterMods, FighterSetup } from './types';

/**
 * Resolves skill ranks into fight modifiers (docs/fight.md §7, docs/game-design.md §5). The
 * per-rank numbers come from config (`SKILLS`).
 */
export function deriveMods(ranks: SkillRanks): FighterMods {
  const rank = {} as Record<SkillId, number>;
  const fx = {} as Record<SkillId, number>;
  const perks = {} as Record<PerkId, boolean>;
  for (const s of SKILLS) {
    const r = Math.max(0, Math.min(MAX_RANK, Math.floor(ranks[s.id] ?? 0)));
    rank[s.id] = r;
    fx[s.id] = s.perRank * r;
    perks[s.perks[5]] = r >= 5;
    perks[s.perks[10]] = r >= 10;
  }
  return {
    damageMul: 1 + fx.power,
    windupMul: 1 - fx.speed,
    strikeCostMul: 1 - fx.speed,
    reachMul: 1 + fx.reach,
    takedownMul: 1 + fx.takedown,
    downMul: 1 - fx.escape,
    takedownTakenMul: 1 - fx.escape,
    strikeBlockReduction: FIGHT.defend.strikeReduction + GUARD_BLOCK * rank.guard,
    readDamageMul: 1 + fx.guard,
    maxHealthMul: 1 + fx.chin,
    speedMul: 1 + fx.footwork,
    evadeMul: 1 - FOOTWORK_EVADE * rank.footwork,
    staminaRegenMul: 1 + fx.cardio,
    meterGainMul: 1 + fx.focus,
    perks,
  };
}

export function fighterSetup(ranks: SkillRanks, baseHealth?: number): FighterSetup {
  return { ranks: { ...ranks }, mods: deriveMods(ranks), ...(baseHealth ? { baseHealth } : {}) };
}

export interface StrikeTiming {
  duration: number;
  hitStart: number;
  hitEnd: number;
}

/**
 * A strike after the Speed skill (D48): the wind-up shortens, the hit window and the recovery keep
 * their length. A landed strike's follow-up so stays blockable (the cycle never drops under the
 * strike's stun), and the jab still comes out sooner.
 */
function withSpeed(base: StrikeTiming, mods: FighterMods): StrikeTiming {
  const hitStart = base.hitStart * mods.windupMul;
  const shift = base.hitStart - hitStart;
  return { duration: base.duration - shift, hitStart, hitEnd: base.hitEnd - shift };
}

/** Strike timings after the Speed skill. */
export function strikeTiming(mods: FighterMods): StrikeTiming {
  return withSpeed(FIGHT.strike, mods);
}

/** The One-two follow-up's timings (Speed 10), after the Speed skill. */
export function oneTwoTiming(mods: FighterMods): StrikeTiming {
  return withSpeed(PERKS.oneTwo, mods);
}

/** Stamina a move costs: Speed makes strikes cheaper, Workhorse every strike and grapple. */
export function moveCost(mods: FighterMods, move: 'strike' | 'strike2' | 'grapple'): number {
  const work = mods.perks.workhorse ? PERKS.workhorse.costMul : 1;
  if (move === 'grapple') return FIGHT.grapple.cost * work;
  return (move === 'strike' ? FIGHT.strike.cost : PERKS.oneTwo.cost) * mods.strikeCostMul * work;
}

/** Signature timings after the Quick release perk (wind-up shortened, dash length kept). */
export function signatureTiming(mods: FighterMods) {
  const sig = FIGHT.signature;
  const windup = mods.perks.quickRelease ? PERKS.quickRelease.windup : sig.windup;
  const shift = sig.windup - windup;
  return { windup, dashEnd: sig.dashEnd - shift, duration: sig.duration - shift };
}
